import { ApolloError } from '@apollo/client'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { FormEventHandler, useEffect, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'

import {
  useNotificationSubscriptionsQuery,
  useUpdateNotificationSettingsMutation,
  useUpdateNotificationSubscriptionMutation,
  VendorUserDocument,
} from '@/suppliers/_gen/gql'

import useMsgs from '@/gf/hooks/useMsgs'
import useToggle from '@/gf/hooks/useToggle'
import Phone from '@/gf/modules/Phone'
import useGqlClient from '@/suppliers/hooks/useGqlClient'
import useUser from '../../hooks/useUser'
import useVendorUser, { User } from '../../hooks/useVendorUser'

import Subscriptions from '@/dealers/pages/Settings/Notifications/Subscriptions'
import EventNotificationSettings from '@/suppliers/pages/Settings/Notifications/EventNotificationSettings'
import Action from '@/gf/components/Action'
import Card from '@/gf/components/Card'
import Checkbox from '@/gf/components/Checkbox'
import Link from '@/gf/components/LinkOld'
import Spinner from '@/gf/components/Spinner'

const NotificationsInput = ({
  user,
  notify,
  onNotifyChange,
  spinnerLive,
  onFormSubmit,
  toProfileUrl = '/settings/profile',
  showEventSettings = false,
}: {
  user: Pick<User, 'id' | 'name' | 'phoneNumber' | 'smsEnabled'>
  notify: boolean
  onNotifyChange: (notify: boolean) => void
  spinnerLive: boolean
  onFormSubmit: FormEventHandler<HTMLFormElement>
  toProfileUrl?: string
  showEventSettings?: boolean
}) => {
  const [_, msgr] = useMsgs()
  const client = useGqlClient()
  const [updateNotifSubMutation] = useUpdateNotificationSubscriptionMutation({ client })

  const queryData = useNotificationSubscriptionsQuery({
    variables: { userId: user.id },
    client,
  }).data

  const notifications = queryData?.notifications
  const notificationSubscriptions = queryData?.store.users[0].notificationSubscriptions

  if (!notificationSubscriptions) return null

  const updateNotifSub = (notificationId: string, channel: string, scope: string) => {
    msgr.add('Subscription saved.', 'positive')

    updateNotifSubMutation({
      variables: { userId: user.id, notificationId, channel, scope },
    }).catch(() => msgr.addUnknownError())
  }

  return (
    <form className="space-y-4" onSubmit={onFormSubmit}>
      <Card title="Notifications">
        <Card.Section>
          <div className="space-y-6">
            <div className="flex items-center gap-4 bg-gray-50 px-4 py-2">
              <Checkbox
                id="notify"
                name="Notify"
                value="notify"
                checked={notify}
                onChange={(e) => onNotifyChange(e.target.checked)}
              >
                Receive email for all notifications
              </Checkbox>

              <Action.P size="sm" type="submit" performing={spinnerLive}>
                Save
              </Action.P>
            </div>

            {showEventSettings && <EventNotificationSettings userId={user.id} notify={notify} />}

            <div className="flex gap-4 items-center text-sm">
              <span>
                Phone number: {user.phoneNumber ? Phone.format(user.phoneNumber) : 'None'}
              </span>

              <Link to={toProfileUrl}>Edit</Link>

              {!user.phoneNumber && (
                <RouterLink
                  className="px-2 py-1 flex gap-1 items-center rounded-md text-sm text-yellow-700 bg-yellow-50"
                  to={toProfileUrl}
                >
                  <ExclamationTriangleIcon className="w-6 h-6" />
                  Text message notifications require a phone number
                </RouterLink>
              )}
            </div>

            <Subscriptions
              notifications={notifications}
              notificationSubscriptions={notificationSubscriptions}
              update={updateNotifSub}
            />
          </div>
        </Card.Section>
      </Card>
    </form>
  )
}

const Notifications = ({ userId }: { userId: string }) => {
  const [_msgs, msgsMgr] = useMsgs()
  const [spinnerLive, spinnerToggler] = useToggle()
  const { data, error } = useUser(userId)
  const user = data?.user
  const gqlClient = useGqlClient()
  const [updateNotificationSettings] = useUpdateNotificationSettingsMutation({
    client: gqlClient,
    refetchQueries: [useUser.Query],
  })
  const [smsEnabled, setSmsEnabled] = useState(user?.smsEnabled || false)
  const [notify, setNotify] = useState(user?.notify || false)

  useEffect(() => {
    if (user) {
      setSmsEnabled(user.smsEnabled)
      setNotify(user.notify)
    }
  }, [user])

  const onFormSubmit: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault()
    if (!user) return
    spinnerToggler.on()
    updateNotificationSettings({
      variables: { smsEnabled, notify, vendorId: null, vendorUserId: null },
    })
      .then(() => {
        msgsMgr.add('Updated notification settings', 'positive')
      })
      .catch((err: ApolloError) => {
        msgsMgr.add(err.message, 'negative')
      })
      .finally(() => {
        spinnerToggler.off()
      })
  }

  return error ? (
    <p className="text-sm text-gray-900">Something went wrong, please contact support.</p>
  ) : !user ? (
    <Spinner />
  ) : (
    <NotificationsInput
      user={user}
      notify={notify}
      onNotifyChange={setNotify}
      spinnerLive={spinnerLive}
      onFormSubmit={onFormSubmit}
      showEventSettings
    />
  )
}

const VendorNotifications = ({ vendorId, userId }: { vendorId: string; userId?: string }) => {
  const [_msgs, msgsMgr] = useMsgs()
  const [spinnerLive, spinnerToggler] = useToggle()
  const { data, error } = useVendorUser(vendorId, userId)
  const user = data?.vendorUser
  const gqlClient = useGqlClient()
  const [updateNotificationSettings] = useUpdateNotificationSettingsMutation({
    client: gqlClient,
    refetchQueries: [VendorUserDocument],
  })
  const [smsEnabled, setSmsEnabled] = useState(user?.smsEnabled || false)
  const [notify, setNotify] = useState(user?.notify || false)

  useEffect(() => {
    if (user) {
      setSmsEnabled(user.smsEnabled)
      setNotify(user.notify)
    }
  }, [user])

  const onFormSubmit: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault()
    if (!user) return
    spinnerToggler.on()
    updateNotificationSettings({
      variables: { smsEnabled, notify, vendorId, vendorUserId: userId ?? null },
    })
      .then(() => {
        msgsMgr.add('Updated notification settings', 'positive')
      })
      .catch((err: ApolloError) => {
        msgsMgr.add(err.message, 'negative')
      })
      .finally(() => {
        spinnerToggler.off()
      })
  }

  return error ? (
    <p className="text-sm text-gray-900">Something went wrong, please contact support.</p>
  ) : !user ? (
    <Spinner />
  ) : (
    <NotificationsInput
      user={user}
      notify={notify}
      onNotifyChange={setNotify}
      spinnerLive={spinnerLive}
      onFormSubmit={onFormSubmit}
      toProfileUrl={`/vendor/${vendorId}/settings/profile`}
    />
  )
}

Notifications.VendorNotifications = VendorNotifications
export default Notifications
